/**
 * Textos del paso de confirmación de Modificación de datos.
 *
 * Viven fuera de la pantalla —mismo criterio que `modificaciones-filtro.ts`—
 * para poder testearlos sin montar el componente.
 *
 * El botón antes decía siempre "Excluir las que tienen error y continuar con
 * N", incluso cuando no había ningún error: prometía una exclusión inexistente
 * y hacía dudar de un archivo que estaba perfecto. Ahora el botón dice qué hace
 * y con cuántas, y lo que queda afuera se explica abajo, con el conteo real.
 */

export interface ConteoConfirmacion {
  /** Listas para enviar, antes de descontar las tildadas a mano. */
  aceptadas: number;
  /** Rechazadas por la validación: nunca se envían. */
  rechazadas: number;
  /** Tildadas a mano por el usuario para dejarlas afuera. */
  excluidas: number;
  /** Ya tienen ese valor en Binary: tampoco se envían. */
  sinCambio?: number;
}

/** Cuántas modificaciones se van a enviar de verdad. */
export function aEnviar(c: ConteoConfirmacion): number {
  return Math.max(0, c.aceptadas - c.excluidas);
}

/** Enumera en castellano: "a", "a y b", "a, b y c". */
function enumerar(partes: string[]): string {
  if (partes.length <= 1) return partes[0] ?? '';
  return `${partes.slice(0, -1).join(', ')} y ${partes[partes.length - 1]}`;
}

/**
 * Etiqueta del botón de confirmar.
 *
 * Nunca menciona exclusiones: el número ya las descontó, y anunciarlas cuando
 * no las hay es lo que confundía.
 */
export function textoConfirmar(c: ConteoConfirmacion): string {
  const n = aEnviar(c);
  if (n === 0) return 'No hay nada para enviar';
  if (n === 1) return 'Enviar 1 modificación a Binary';
  return `Enviar ${n} modificaciones a Binary`;
}

/**
 * La línea de abajo del botón: qué queda afuera y por qué.
 *
 * Se listan los tres motivos por separado —error, tildada a mano, sin cambio—
 * porque se arreglan distinto. Si no queda nada afuera, no se inventa un
 * problema: solo se ofrece el control.
 */
export function textoExclusiones(c: ConteoConfirmacion): string {
  const sinCambio = c.sinCambio ?? 0;
  const fuera: string[] = [];
  if (c.rechazadas > 0) fuera.push(`${c.rechazadas} con error`);
  if (c.excluidas > 0) fuera.push(`${c.excluidas} que dejaste afuera`);
  if (sinCambio > 0) fuera.push(`${sinCambio} sin cambio`);

  if (fuera.length === 0) return 'Tildá una fila si querés dejarla afuera del envío.';

  const total = c.rechazadas + c.excluidas + sinCambio;
  const verbo = total === 1 ? 'No se envía' : 'No se envían';
  return `${verbo} ${enumerar(fuera)}. Tildá una fila si querés dejar alguna más afuera.`;
}
